(() => {
  if (customElements.get('xuk-credentials-gallery')) return;

  class XukCredentialsGallery extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('[data-credentials-dialog]');
      if (!this.dialog) return;
      this.viewerImage = this.querySelector('[data-credentials-dialog-image]');
      this.viewerTitle = this.querySelector('[data-credentials-dialog-title]');
      this.position = this.querySelector('[data-credentials-position]');
      this.viewport = this.querySelector('[data-credentials-viewport]');
      this.placeholder = this.querySelector('[data-credentials-dialog-placeholder]');
      this.zoomLabel = this.querySelector('[data-credentials-zoom]');
      this.activeIndex = 0;
      this.scale = 1;

      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-credentials-open]');
        if (card) {
          this.trigger = card;
          this.activeIndex = this.cards.indexOf(card);
          this.dialog.showModal();
          this.lockScroll();
          this.showDocument(this.activeIndex);
          return;
        }
        const tool = event.target.closest('[data-credentials-action]');
        if (!tool || tool.disabled) return;
        switch (tool.dataset.credentialsAction) {
          case 'close': this.dialog.close(); break;
          case 'previous': this.showDocument(this.activeIndex - 1); break;
          case 'next': this.showDocument(this.activeIndex + 1); break;
          case 'zoom-in': this.changeZoom(0.25); break;
          case 'zoom-out': this.changeZoom(-0.25); break;
          case 'reset': this.scale = 1; this.fitImage(); break;
        }
      }, { signal });

      this.addEventListener('error', (event) => {
        const image = event.target;
        const button = image.closest?.('[data-credentials-open]');
        if (button) {
          image.hidden = true;
          button.dataset.unavailable = 'true';
          const missing = button.querySelector('.xuk-credentials__placeholder');
          if (missing) missing.hidden = false;
        }
      }, { signal, capture: true });

      window.addEventListener('keydown', (event) => {
        if (!this.dialog.open) return;
        if (event.key === 'Tab') {
          const tools = [...this.dialog.querySelectorAll('button:not(:disabled)')];
          const index = tools.indexOf(document.activeElement);
          if (index === -1 || (!event.shiftKey && index === tools.length - 1) ||
              (event.shiftKey && index === 0)) {
            event.preventDefault();
            tools[event.shiftKey ? tools.length - 1 : 0]?.focus();
          }
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showDocument(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });

      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.dialog.close();
        }
      }, { signal });

      this.dialog.addEventListener('close', () => {
        this.unlockScroll();
        this.trigger?.focus({ preventScroll: true });
      }, { signal });
      this.viewerImage.addEventListener('load', () => {
        this.viewport.removeAttribute('aria-busy');
        this.viewerImage.hidden = false;
        this.fitImage();
      }, { signal });
      this.viewerImage.addEventListener('error', () => this.showMissingImage(), { signal });
      window.addEventListener('resize', () => {
        if (this.dialog.open) this.fitImage();
      }, { signal });

      const manifest = this.dataset.manifest;
      if (manifest) this.loadPreview(manifest, signal);
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.unlockScroll();
      }
      this.abort?.abort();
    }

    get cards() {
      return [...this.querySelectorAll('[data-credentials-open]')];
    }

    async loadPreview(url, signal) {
      try {
        const response = await fetch(url, { signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Manifest unavailable');
        const { documents } = await response.json();
        const grid = this.querySelector('[data-credentials-grid]');
        const fragment = document.createDocumentFragment();
        for (const documentData of documents) {
          if (documentData.visible === false) continue;
          const article = document.createElement('article');
          article.className = 'xuk-credentials__card';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'xuk-credentials__open';
          button.dataset.credentialsOpen = '';
          button.dataset.id = documentData.id;
          button.dataset.fullImage = documentData.image;
          button.dataset.previewQuality = documentData.preview_quality || 'auto';
          button.setAttribute('aria-label', `View ${documentData.name}`);
          const frame = document.createElement('span');
          frame.className = 'xuk-credentials__image';
          const image = document.createElement('img');
          image.src = documentData.image;
          image.alt = documentData.name;
          image.width = documentData.width;
          image.height = documentData.height;
          image.style.setProperty('--source-width', `${documentData.width}px`);
          image.style.setProperty('--source-height', `${documentData.height}px`);
          image.loading = fragment.childElementCount < 4 ? 'eager' : 'lazy';
          image.decoding = 'async';
          const missing = document.createElement('span');
          missing.className = 'xuk-credentials__placeholder';
          missing.hidden = true;
          missing.textContent = 'Certificate image unavailable';
          frame.append(image, missing);
          const name = document.createElement('span');
          name.className = 'xuk-credentials__name';
          name.textContent = documentData.name;
          button.append(frame, name);
          if (documentData.status) {
            const status = document.createElement('span');
            status.className = 'xuk-credentials__status';
            status.textContent = documentData.status;
            button.appendChild(status);
          }
          article.appendChild(button);
          fragment.appendChild(article);
        }
        grid.replaceChildren(fragment);
        this.dataset.ready = 'true';
      } catch (error) {
        if (error.name === 'AbortError') return;
        const message = this.querySelector('[data-credentials-grid]');
        message.textContent = 'Certificate images unavailable.';
        this.dataset.ready = 'error';
      }
    }

    showDocument(index) {
      const cards = this.cards;
      if (!cards[index]) return;
      this.activeIndex = index;
      this.scale = 1;
      const button = cards[index];
      const image = button.querySelector('img');
      this.previewQuality = button.dataset.previewQuality || 'auto';
      this.dialog.dataset.previewQuality = this.previewQuality;
      this.maxScale = 1;
      const status = button.querySelector('.xuk-credentials__status')?.textContent;
      this.viewerTitle.textContent = button.querySelector('.xuk-credentials__name').textContent;
      this.position.textContent = `${index + 1} / ${cards.length}`;
      this.querySelector('[data-credentials-action="previous"]').disabled = index === 0;
      this.querySelector('[data-credentials-action="next"]').disabled = index === cards.length - 1;
      if (this.dialog.contains(document.activeElement) && document.activeElement.disabled) {
        this.querySelector('[data-credentials-action="close"]').focus();
      }
      this.viewerImage.hidden = true;
      this.viewerImage.style.width = '';
      this.viewerImage.style.height = '';
      this.viewerImage.alt = this.viewerTitle.textContent;
      this.placeholder.hidden = true;
      this.placeholder.textContent = '';
      const viewerStatus = this.querySelector('[data-credentials-viewer-status]');
      viewerStatus.hidden = !status;
      viewerStatus.textContent = status || '';
      if (!image || button.dataset.unavailable === 'true') {
        this.showMissingImage();
        return;
      }
      this.viewerImage.width = image.width;
      this.viewerImage.height = image.height;
      this.viewport.setAttribute('aria-busy', 'true');
      this.viewerImage.src = button.dataset.fullImage || image.currentSrc || image.src;
      this.updateZoomControls();
      requestAnimationFrame(() => {
        if (this.viewerImage.complete && this.viewerImage.naturalWidth) {
          this.viewport.removeAttribute('aria-busy');
          this.viewerImage.hidden = false;
          this.fitImage();
        }
      });
    }

    showMissingImage() {
      this.viewerImage.hidden = true;
      this.viewerImage.removeAttribute('src');
      this.viewport.removeAttribute('aria-busy');
      this.placeholder.hidden = false;
      this.placeholder.textContent = 'Certificate image unavailable.';
      this.scale = 1;
      this.querySelector('[data-credentials-viewer-status]').hidden = true;
      this.updateZoomControls();
    }

    fitImage() {
      if (this.viewerImage.hidden || !this.viewerImage.naturalWidth) return;
      const padding = matchMedia('(max-width: 639px)').matches ? 32 : 48;
      const naturalWidth = this.viewerImage.naturalWidth;
      const naturalHeight = this.viewerImage.naturalHeight;
      const isThumbnail = this.previewQuality === 'thumbnail' ||
        (this.previewQuality === 'auto' && Math.max(naturalWidth, naturalHeight) < 500);
      this.dialog.dataset.previewQuality = isThumbnail ? 'thumbnail' : 'document';
      // Never turn a fit-to-window operation into uncontrolled source upscaling.
      const fit = Math.min(
        Math.max(1, this.viewport.clientWidth - padding) / naturalWidth,
        Math.max(1, this.viewport.clientHeight - padding) / naturalHeight, 1);
      this.maxScale = Math.min(3, (isThumbnail ? 1.5 : 1) / fit);
      this.scale = Math.min(this.scale, this.maxScale);
      this.viewerImage.style.width = `${naturalWidth * fit * this.scale}px`;
      this.viewerImage.style.height = `${naturalHeight * fit * this.scale}px`;
      this.viewport.scrollTop = Math.max(0, (this.viewport.scrollHeight - this.viewport.clientHeight) / 2);
      this.viewport.scrollLeft = Math.max(0, (this.viewport.scrollWidth - this.viewport.clientWidth) / 2);
      this.updateZoomControls();
    }

    changeZoom(delta) {
      this.scale = Math.min(this.maxScale || 1, Math.max(1, this.scale + delta));
      this.fitImage();
    }

    updateZoomControls() {
      const unavailable = this.viewerImage.hidden;
      this.zoomLabel.textContent = `${Math.round(this.scale * 100)}%`;
      this.querySelector('[data-credentials-action="zoom-in"]').disabled = unavailable || this.scale >= this.maxScale - 0.001;
      this.querySelector('[data-credentials-action="zoom-out"]').disabled = unavailable || this.scale <= 1;
      this.querySelector('[data-credentials-action="reset"]').disabled = unavailable || this.scale === 1;
    }

    lockScroll() {
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.savedOverflow !== undefined) {
        document.documentElement.style.overflow = this.savedOverflow;
        this.savedOverflow = undefined;
      }
    }
  }

  customElements.define('xuk-credentials-gallery', XukCredentialsGallery);
})();
